import { useEffect, useRef } from 'react';
import { Mic, MicOff } from 'lucide-react';
import { useSpeechToText } from '@/lib/useSpeechToText';

/**
 * 带语音输入的 input/textarea(web-b 版本,与 web-a 同款,仅替换颜色为 teamB 主题)
 * - 右侧(下方)有麦克风按钮,点击开始/结束录音
 * - 录音停止后,识别文本会自动 append 到 value(空格分隔)
 * - isSupported=false 时,按钮置灰并显示降级提示
 */
export function VoiceInput({
  value,
  onChange,
  placeholder,
  maxLength,
  multiline = false,
  rows = 3,
  className = '',
  ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  maxLength?: number;
  multiline?: boolean;
  rows?: number;
  className?: string;
  ariaLabel?: string;
}) {
  const speech = useSpeechToText({ lang: 'zh-CN' });
  // 用 ref 跟踪最新的 value,避免 useEffect 闭包旧值
  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  // 录音结束后把识别文本追加到 value;同时监听 text,避免 stop 先于 onresult 的竞态
  useEffect(() => {
    if (speech.status === 'idle' && speech.text) {
      const current = valueRef.current ?? '';
      onChange((current ? `${current} ${speech.text}` : speech.text).trim());
      speech.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speech.status, speech.text]);

  const onVoiceClick = () => {
    if (speech.status === 'recording') speech.stop();
    else speech.start();
  };

  const baseInputClass = `input ${multiline ? 'min-h-[80px] resize-none' : ''} ${className}`;

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        {multiline ? (
          <textarea
            className={`${baseInputClass} flex-1`}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            maxLength={maxLength}
            rows={rows}
            aria-label={ariaLabel}
          />
        ) : (
          <input
            className={`${baseInputClass} flex-1`}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            maxLength={maxLength}
            aria-label={ariaLabel}
          />
        )}
        <button
          type="button"
          onClick={onVoiceClick}
          disabled={!speech.isSupported}
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md transition ${
            speech.status === 'recording'
              ? 'bg-red-500 text-white animate-pulse'
              : speech.isSupported
                ? 'bg-teamB-primary text-white'
                : 'bg-slate-200 text-slate-400'
          }`}
          aria-label={speech.status === 'recording' ? '停止录音' : '开始口述'}
          title={speech.isSupported ? '点击开始/结束口述' : '当前浏览器不支持语音'}
        >
          {speech.status === 'recording' ? (
            <MicOff className="h-4 w-4" />
          ) : (
            <Mic className="h-4 w-4" />
          )}
        </button>
      </div>
      <div className="flex items-center justify-between text-[11px] text-slate-400">
        <span>
          {speech.status === 'recording'
            ? '🔴 正在听…说完再点一下结束'
            : speech.isSupported
              ? '点击 🎤 麦克风开始口述(Chrome/Edge)'
              : '当前浏览器不支持语音识别,请直接输入'}
        </span>
        {maxLength ? <span>{value.length} / {maxLength}</span> : null}
      </div>
      {speech.error && <p className="text-[11px] text-red-600">{speech.error}</p>}
    </div>
  );
}
